import { useEffect, useRef, type MutableRefObject } from "react";
import { ExportButton } from "@/components/ExportButton";
import type { Exporter, Identifier, RaRecord } from "react-admin";
import {
  Datagrid,
  List,
  TextField,
  FunctionField,
  SimpleList,
  useListContext,
  usePermissions,
  Pagination,
  TopToolbar,
} from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import { formatListDate } from "@/utils/date-format";
import DisplayDateField from "@/components/DisplayDateField";
import { Box, IconButton, Link, useMediaQuery } from "@mui/material";
import { exportToExcel } from "@/utils/export-to-excel";
import { isSabilTallyConfigReady } from "@/types/tally-config";
import type { ExcelColumn } from "@/types/excel";
import { formatINR } from "@/utils/app-formatters";
import { hasPermission } from "@/utils/permission-utils";
import TallyExportButton from "@/components/TallyExportButton";
import StackedDatagridCell from "@/components/StackedDatagridCell";
import SabilReceiptFilterBar from "./SabilReceiptFilters";
import SabilReceiptPaymentSummary from "./SabilReceiptPaymentSummary";
import {
  type SabilReceiptRow,
  DEFAULT_SABIL_TYPE_FILTER,
  getFilterFromURL,
  navigateToSabilShow,
  openReceiptPrint,
  receiptDisplayName,
  sabilDataId,
  sabilExportFilenameSuffix,
  sabilNoLabel,
  sabilTypeLabel,
} from "./sabil-receipt-utils";

const sabilLinkSx = {
  fontWeight: 600,
  border: 0,
  background: "none",
  padding: 0,
  font: "inherit",
  color: "primary.main",
  cursor: "pointer",
  textAlign: "left" as const,
};

const ReceiptDownloadAction = ({
  recordId,
  stopPropagation = false,
}: {
  recordId: string | number;
  stopPropagation?: boolean;
}) => (
  <IconButton
    size="small"
    color="primary"
    aria-label="Download receipt"
    onClick={(event) => {
      if (stopPropagation) {
        event.stopPropagation();
        event.preventDefault();
      }
      openReceiptPrint(recordId);
    }}
  >
    <DownloadIcon fontSize="small" />
  </IconButton>
);

const SabilNoCell = ({
  record,
  canViewSabil,
}: {
  record: SabilReceiptRow;
  canViewSabil: boolean;
}) => {
  const id = sabilDataId(record);
  const label = sabilNoLabel(record);
  if (!id || label === "—") return <>{label}</>;
  if (canViewSabil) {
    return (
      <Link
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigateToSabilShow(id);
        }}
        sx={sabilLinkSx}
      >
        {label}
      </Link>
    );
  }
  return <>{label}</>;
};

const simpleListSx = {
  "& .MuiListItemButton-root": { alignItems: "center" },
  "& .MuiListItemIcon-root": { minWidth: "auto", justifyContent: "center", alignItems: "center" },
} as const;

const ReceiptListBody = ({
  isSmall,
  canViewSabil,
  canEdit,
}: {
  isSmall: boolean;
  canViewSabil: boolean;
  canEdit: boolean;
}) => {
  if (isSmall) {
    return (
      <SimpleList
        primaryText={(record) => String(record.receiptNo ?? "—")}
        secondaryText={(record) => {
          const row = record as SabilReceiptRow;
          const dateLabel = row.receiptDate ? formatListDate(row.receiptDate as string) : "—";
          return (
            <>
              {receiptDisplayName(row)}
              {row.sabilData?.itsNo ? ` · ITS ${row.sabilData.itsNo}` : ""}
              <br />
              {sabilTypeLabel(row.sabilData?.sabilType)} · Sabil {sabilNoLabel(row)} · {dateLabel} ·{" "}
              {formatINR(row.amount)} · {String(row.paymentMode ?? "—")}
            </>
          );
        }}
        tertiaryText={(record) => {
          const remarks = String((record as SabilReceiptRow).remarks ?? "").trim();
          return remarks || "—";
        }}
        linkType={canEdit ? "edit" : false}
        rightIcon={(record) => <ReceiptDownloadAction recordId={record.id} stopPropagation />}
        sx={simpleListSx}
        rowSx={() => ({ borderBottom: 1, borderBottomColor: "divider" })}
      />
    );
  }

  return (
    <Datagrid rowClick={canEdit ? "edit" : false} bulkActionButtons={false}>
      <TextField source="receiptNo" />
      <FunctionField
        label="Sabil"
        sortBy="sabilData.sabilType"
        render={(record: SabilReceiptRow) => (
          <StackedDatagridCell
            primary={<SabilNoCell record={record} canViewSabil={canViewSabil} />}
            secondary={sabilTypeLabel(record.sabilData?.sabilType)}
          />
        )}
      />
      <FunctionField
        label="Name"
        sortBy="sabilData.name"
        render={(record: SabilReceiptRow) => (
          <StackedDatagridCell
            primary={receiptDisplayName(record)}
            secondary={record.sabilData?.itsNo ? `ITS ${record.sabilData.itsNo}` : "—"}
          />
        )}
      />
      <FunctionField
        label="Payment"
        sortBy="amount"
        render={(record: SabilReceiptRow) => (
          <StackedDatagridCell
            align="right"
            primaryBold
            primary={formatINR(record.amount)}
            secondary={String(record.paymentMode ?? "—")}
          />
        )}
      />
      <DisplayDateField source="receiptDate" label="Receipt date" emptyText="—" />
      <TextField source="remarks" emptyText="—" />
      <FunctionField
        label="Download"
        source="formNo"
        sortable={false}
        render={(record: SabilReceiptRow) => (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <ReceiptDownloadAction recordId={record.id} stopPropagation />
          </Box>
        )}
      />
    </Datagrid>
  );
};

const listToolbarSx = {
  justifyContent: "flex-start",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  rowGap: 1,
  width: "100%",
  px: { xs: 1, sm: 1.5 },
  py: 0.75,
  minHeight: 48,
  "& .MuiButton-root": {
    minHeight: 32,
    py: 0.5,
    px: 1.25,
    fontSize: "0.8125rem",
  },
  "& .MuiIconButton-root": { width: 32, height: 32 },
} as const;

const listToolbarActionsSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  rowGap: 1,
  ml: "auto",
} as const;

const ExportFilterSync = ({ sabilTypeRef }: { sabilTypeRef: MutableRefObject<string> }) => {
  const { filterValues } = useListContext();
  useEffect(() => {
    if (filterValues?.sabilType) {
      sabilTypeRef.current = String(filterValues.sabilType);
    }
  }, [filterValues?.sabilType, sabilTypeRef]);
  return null;
};

const SabilListActions = () => {
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "sabilReceipts.view");

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
      <TopToolbar sx={listToolbarSx}>
        <Box sx={listToolbarActionsSx}>
          {canView ? <ExportButton size="small" /> : null}
          {canView ? (
            <TallyExportButton
              module="sabil"
              filenamePrefix="sabil-tally"
              isConfigReady={isSabilTallyConfigReady}
            />
          ) : null}
        </Box>
      </TopToolbar>
      <SabilReceiptFilterBar />
    </Box>
  );
};

const receiptExportColumns: ExcelColumn<RaRecord<Identifier>>[] = [
  { header: "Receipt No", field: "receiptNo", width: 15 },
  {
    header: "Sabil No",
    field: (rec) => (rec as SabilReceiptRow)?.sabilData?.sabilNo || "",
    width: 15,
  },
  {
    header: "Sabil Type",
    field: (rec) => sabilTypeLabel((rec as SabilReceiptRow)?.sabilData?.sabilType),
    width: 18,
  },
  {
    header: "HOF ITS",
    field: (rec) => (rec as SabilReceiptRow)?.sabilData?.itsNo || "",
    width: 12,
  },
  {
    header: "Name",
    field: (rec) => receiptDisplayName(rec as SabilReceiptRow) || "",
    width: 30,
  },
  {
    header: "Amount",
    field: "amount",
    width: 12,
    formatter: (_rec, v) => formatINR(v),
  },
  {
    header: "Receipt Date",
    field: "receiptDate",
    width: 15,
    formatter: (_rec, v) => (v ? formatListDate(String(v)) : ""),
  },
  { header: "Payment Mode", field: "paymentMode", width: 15 },
  { header: "Remarks", field: "remarks", width: 28 },
];

const SabilReceiptList = () => {
  const { permissions } = usePermissions();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const exportSabilTypeRef = useRef(DEFAULT_SABIL_TYPE_FILTER);

  const exporter = (records: RaRecord[]) => {
    const tabName = sabilExportFilenameSuffix(exportSabilTypeRef.current);
    return exportToExcel(receiptExportColumns, records, {
      filenamePrefix: `sabilreceipts-${tabName}`,
      sheetName: "Sabil Receipts",
    });
  };

  const canView = hasPermission(permissions, "sabilReceipts.view");
  const canViewSabil = hasPermission(permissions, "sabil.view");
  const canEdit = hasPermission(permissions, "sabilReceipts.edit");

  return (
    <List
      sort={{ field: "receiptNo", order: "DESC" }}
      filterDefaultValues={getFilterFromURL()}
      actions={<SabilListActions />}
      exporter={canView ? (exporter as unknown as Exporter<RaRecord<Identifier>>) : false}
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      sx={{
        "& .RaList-actions": {
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
        },
        "& .RaList-actions > .MuiToolbar-root": listToolbarSx,
      }}
    >
      <ExportFilterSync sabilTypeRef={exportSabilTypeRef} />
      <SabilReceiptPaymentSummary />
      <ReceiptListBody isSmall={isSmall} canViewSabil={canViewSabil} canEdit={canEdit} />
    </List>
  );
};

export default SabilReceiptList;
